/* billingTermsRules.js -- SUBSCRIPTION BILLING TERMS: THE STORED SHAPES AND THEIR STRUCTURAL RULES -- ONE CLIENT OWNER
 * (billing terms P1, 2026-10-26; design docs/BILLING-TERMS.md (a)-(l) + Brian's 10-26 decisions (m)-(p);
 * diffs/billing-terms-investigation.txt Q4 / Q-B1..B11; diffs/billing-terms-p1-build.txt).
 *
 * FOUR NULLABLE, DEFAULTLESS COLUMNS ON ae_quota_settings (NULL = absent = today's engine, exactly):
 *   billing_terms jsonb          { mix, churn, renewal_mode, manual_renewals, existing_book }
 *     mix              { term: share }                  shares 0..1 summing to 1 (+/- MIX_TOLERANCE)        (b) (c)
 *     churn            { term: rate }                   0..1; a missing term uses ae_quota_settings.churn_rate (d)
 *     renewal_mode     { term: 'auto' | 'manual' }      a missing term uses ae_quota_settings.renewal_mode     (m)
 *     manual_renewals  { term: { 'YYYY-MM': amount } }  NON-ANNUAL terms only (annual keeps the manual_renewals column)
 *     existing_book    { term: { billings: {..}, anniversary: {..} } }   NON-ANNUAL terms only -- annual's book stays the
 *                      existing_total_acv column (it is both the annual billings and the annual anniversary row)     (h)
 *   renewal_adjustments jsonb    [ { term, month, amount } ]     (n) amount in ANNUAL CONTRACT VALUE for every term (Q-B1);
 *                                                                 a FORECAST month; finite and non-zero
 *   unbilled_collections jsonb   [ { month, cash_received, cleared } ]   (o) a FORECAST month; cash_received >= 0 (no refunds,
 *                                                                 Q-B6); cleared null (= the default) or >= 0
 *   unbilled_ar_bs_line_key varchar(100)   (p) the linked current-asset line -- set ONLY by its own super-admin route
 *                                                 (PUT .../ae-quota-settings/unbilled-ar-link, Q-B5)
 * "Forecast month" = after companies.close_month (Q-B6: nothing dated in a closed month).
 *
 * THE "OFF" FORMS -- COALESCE-on-omit cannot clear a column back to NULL (CLAUDE.md section 5), so each column has a NON-NULL
 * form that MEANS ABSENT, behaviourally identical to NULL:
 *   billing_terms {}  and  { mix: { annual: 1 } }  (and any shape whose only content is that)    renewal_adjustments []
 *   unbilled_collections []                                                                       the link: null (its route)
 *
 * THE SERVER COPIES THESE RULES (a server cannot import client code -- THE WALL); verify-wall-mirrors.js pair M19 asserts the
 * two agree. Validators return null (accepted) or { code, error } (refused, named). STRUCTURAL ONLY: no engine reads these yet.
 * Plain ASCII on purpose (CLAUDE.md 12.5 rule 55). PURE. */

export const TERMS = [
  { key: 'annual', cycle: 12 },
  { key: 'semi_annual', cycle: 6 },
  { key: 'quarterly', cycle: 3 },
  { key: 'monthly', cycle: 1 },
];
export const TERM_KEYS = ['annual', 'semi_annual', 'quarterly', 'monthly'];
export const NON_ANNUAL_TERM_KEYS = ['semi_annual', 'quarterly', 'monthly'];
export const RENEWAL_MODES = ['auto', 'manual'];
export const MIX_TOLERANCE = 0.0001;
export const BILLING_TERMS_KEYS = ['mix', 'churn', 'renewal_mode', 'manual_renewals', 'existing_book'];
export const EXISTING_BOOK_KEYS = ['billings', 'anniversary'];
export const ADJUSTMENT_KEYS = ['term', 'month', 'amount'];
export const COLLECTION_KEYS = ['month', 'cash_received', 'cleared'];
export const UNBILLED_AR_SUBCATEGORIES = ['other_receivable', 'other_current_asset'];

const isPlainObject = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const isFiniteNumber = v => typeof v === 'number' && Number.isFinite(v);
export function isMonthKey(s) {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}$/.test(s)) return false;
  const mo = Number(s.slice(5, 7));
  return mo >= 1 && mo <= 12;
}
const refuse = (code, error) => ({ code, error });
const unknownKey = (obj, allowed) => Object.keys(obj).find(k => !allowed.includes(k));

// a { 'YYYY-MM': amount >= 0 } map, at `path`
function monthMapRefusal(map, path) {
  if (!isPlainObject(map)) return refuse('BILLING_TERMS_NOT_OBJECT', path + ' must be an object of month -> amount.');
  for (const [m, v] of Object.entries(map)) {
    if (!isMonthKey(m)) return refuse('MONTH_INVALID', path + ': "' + m + '" is not a month (YYYY-MM).');
    if (!isFiniteNumber(v) || v < 0) return refuse('AMOUNT_INVALID', path + '.' + m + ' must be a number of 0 or more.');
  }
  return null;
}
// a { term: value } map whose keys must be terms (`allowedTerms`), at `path`
function termMapRefusal(map, path, allowedTerms) {
  if (!isPlainObject(map)) return refuse('BILLING_TERMS_NOT_OBJECT', path + ' must be an object keyed by billing term.');
  for (const t of Object.keys(map)) {
    if (!TERM_KEYS.includes(t)) return refuse('BILLING_TERMS_UNKNOWN_TERM', path + ': "' + t + '" is not a billing term (annual, semi_annual, quarterly, monthly).');
    if (!allowedTerms.includes(t)) return refuse('BILLING_TERMS_TERM_NOT_ALLOWED', path + ': ' + t + ' is not allowed here -- the annual term keeps its own column.');
  }
  return null;
}

/* billing_terms: null / undefined (omitted) is accepted -- the caller does not own the field. */
export function validateBillingTerms(v) {
  if (v === undefined || v === null) return null;
  if (!isPlainObject(v)) return refuse('BILLING_TERMS_NOT_OBJECT', 'billing_terms must be an object.');
  const uk = unknownKey(v, BILLING_TERMS_KEYS);
  if (uk) return refuse('BILLING_TERMS_UNKNOWN_KEY', 'billing_terms.' + uk + ' is not a billing-terms field.');
  if (v.mix !== undefined) {
    const r = termMapRefusal(v.mix, 'billing_terms.mix', TERM_KEYS); if (r) return r;
    let sum = 0;
    for (const [t, s] of Object.entries(v.mix)) {
      if (!isFiniteNumber(s) || s < 0 || s > 1) return refuse('MIX_SHARE_INVALID', 'billing_terms.mix.' + t + ' must be a share between 0 and 1.');
      sum += s;
    }
    if (Object.keys(v.mix).length && Math.abs(sum - 1) > MIX_TOLERANCE) return refuse('MIX_SUM_INVALID', 'The billing-term mix must total 100% (it totals ' + (Math.round(sum * 1000000) / 10000) + '%).');
  }
  if (v.churn !== undefined) {
    const r = termMapRefusal(v.churn, 'billing_terms.churn', TERM_KEYS); if (r) return r;
    for (const [t, c] of Object.entries(v.churn)) if (!isFiniteNumber(c) || c < 0 || c > 1) return refuse('CHURN_INVALID', 'billing_terms.churn.' + t + ' must be a rate between 0 and 1.');
  }
  if (v.renewal_mode !== undefined) {
    const r = termMapRefusal(v.renewal_mode, 'billing_terms.renewal_mode', TERM_KEYS); if (r) return r;
    for (const [t, m] of Object.entries(v.renewal_mode)) if (!RENEWAL_MODES.includes(m)) return refuse('RENEWAL_MODE_INVALID', 'billing_terms.renewal_mode.' + t + ' must be auto or manual.');
  }
  if (v.manual_renewals !== undefined) {
    const r = termMapRefusal(v.manual_renewals, 'billing_terms.manual_renewals', NON_ANNUAL_TERM_KEYS); if (r) return r;
    for (const [t, map] of Object.entries(v.manual_renewals)) { const r2 = monthMapRefusal(map, 'billing_terms.manual_renewals.' + t); if (r2) return r2; }
  }
  if (v.existing_book !== undefined) {
    const r = termMapRefusal(v.existing_book, 'billing_terms.existing_book', NON_ANNUAL_TERM_KEYS); if (r) return r;
    for (const [t, book] of Object.entries(v.existing_book)) {
      if (!isPlainObject(book)) return refuse('BILLING_TERMS_NOT_OBJECT', 'billing_terms.existing_book.' + t + ' must be { billings, anniversary }.');
      const uk2 = unknownKey(book, EXISTING_BOOK_KEYS);
      if (uk2) return refuse('BILLING_TERMS_UNKNOWN_KEY', 'billing_terms.existing_book.' + t + '.' + uk2 + ' is not a book field (billings, anniversary).');
      for (const k of EXISTING_BOOK_KEYS) if (book[k] !== undefined) { const r2 = monthMapRefusal(book[k], 'billing_terms.existing_book.' + t + '.' + k); if (r2) return r2; }
    }
  }
  return null;
}

// a forecast month: after the close. With no close month the question cannot be answered -- refused, named.
function forecastMonthRefusal(m, closeMonth, path, codePrefix) {
  if (!isMonthKey(m)) return refuse(codePrefix + '_MONTH_INVALID', path + '.month must be a month (YYYY-MM).');
  if (!closeMonth) return refuse('CLOSE_MONTH_UNKNOWN', 'The company has no close month, so ' + path + ' cannot be checked against it.');
  if (m <= closeMonth) return refuse(codePrefix + '_MONTH_CLOSED', path + '.month ' + m + ' is not after the close (' + closeMonth + ') -- only forecast months can be adjusted.');
  return null;
}

/* renewal_adjustments: [ { term, month, amount } ]. null / undefined = omitted (accepted). */
export function validateRenewalAdjustments(v, closeMonth) {
  if (v === undefined || v === null) return null;
  if (!Array.isArray(v)) return refuse('RENEWAL_ADJUSTMENTS_NOT_ARRAY', 'renewal_adjustments must be a list.');
  for (let i = 0; i < v.length; i++) {
    const a = v[i], path = 'renewal_adjustments[' + i + ']';
    if (!isPlainObject(a)) return refuse('ADJUSTMENT_INVALID', path + ' must be { term, month, amount }.');
    const uk = unknownKey(a, ADJUSTMENT_KEYS);
    if (uk) return refuse('ADJUSTMENT_INVALID', path + '.' + uk + ' is not an adjustment field (term, month, amount).');
    if (!TERM_KEYS.includes(a.term)) return refuse('ADJUSTMENT_UNKNOWN_TERM', path + '.term must be a billing term (annual, semi_annual, quarterly, monthly).');
    const r = forecastMonthRefusal(a.month, closeMonth, path, 'ADJUSTMENT'); if (r) return r;
    if (!isFiniteNumber(a.amount) || a.amount === 0) return refuse('ADJUSTMENT_AMOUNT_INVALID', path + '.amount must be a non-zero amount of annual contract value.');
  }
  return null;
}

/* unbilled_collections: [ { month, cash_received, cleared } ]. null / undefined = omitted (accepted). */
export function validateUnbilledCollections(v, closeMonth) {
  if (v === undefined || v === null) return null;
  if (!Array.isArray(v)) return refuse('UNBILLED_COLLECTIONS_NOT_ARRAY', 'unbilled_collections must be a list.');
  for (let i = 0; i < v.length; i++) {
    const x = v[i], path = 'unbilled_collections[' + i + ']';
    if (!isPlainObject(x)) return refuse('COLLECTION_INVALID', path + ' must be { month, cash_received, cleared }.');
    const uk = unknownKey(x, COLLECTION_KEYS);
    if (uk) return refuse('COLLECTION_INVALID', path + '.' + uk + ' is not a collection field (month, cash_received, cleared).');
    const r = forecastMonthRefusal(x.month, closeMonth, path, 'COLLECTION'); if (r) return r;
    if (!isFiniteNumber(x.cash_received) || x.cash_received < 0) return refuse('COLLECTION_CASH_INVALID', path + '.cash_received must be an amount of 0 or more (a refund is not a collection).');
    if (x.cleared !== undefined && x.cleared !== null && (!isFiniteNumber(x.cleared) || x.cleared < 0)) return refuse('COLLECTION_CLEARED_INVALID', path + '.cleared must be blank (the default) or an amount of 0 or more.');
  }
  return null;
}

/* The "off" forms: does a stored value MEAN ABSENT (behaviourally identical to NULL)? */
export function billingTermsAbsent(v) {
  if (v === undefined || v === null) return true;
  if (!isPlainObject(v)) return false;
  for (const k of Object.keys(v)) {
    if (k === 'mix') {
      const mix = v.mix;
      if (mix === undefined || mix === null) continue;
      if (!isPlainObject(mix)) return false;
      const ks = Object.keys(mix);
      if (!ks.length) continue;
      if (mix.annual !== 1 || ks.some(t => t !== 'annual' && mix[t] !== 0)) return false;
    } else {
      const x = v[k];
      if (x === undefined || x === null) continue;
      if (!isPlainObject(x) || Object.keys(x).length) return false;
    }
  }
  return true;
}
export const listAbsent = v => v === undefined || v === null || (Array.isArray(v) && v.length === 0);

/* THE UNBILLED-AR LINK (p): which lines may carry it, and the named refusal. The line is one of THIS company's balance-sheet
 * current-asset lines of role accounts_receivable with subcategory other_receivable or other_current_asset -- never trade A/R
 * (its driver is DSO on revenue), never a deferred-revenue line, never the line the accrued-payroll or accrued-commissions link
 * already carries. `links` = { accruedPayrollKey, accruedCommissionsKey }. */
export function unbilledArLeafKeysFromMeta(meta) {
  const out = [];
  for (const [id, m] of Object.entries(isPlainObject(meta) ? meta : {})) {
    if (m && m.role === 'accounts_receivable' && UNBILLED_AR_SUBCATEGORIES.includes(m.subcategory)) out.push(id);
  }
  return out;
}
export function unbilledArLinkRefusal(key, meta, links) {
  const l = links || {};
  const mm = isPlainObject(meta) ? meta : {};
  if (typeof key !== 'string' || !key || key.length > 100 || !Object.prototype.hasOwnProperty.call(mm, key) || !isPlainObject(mm[key])) {
    return refuse('UNBILLED_AR_LINE_INVALID', 'unbilled_ar line "' + String(key) + '" is not a balance-sheet line of this company.');
  }
  const m = mm[key];
  if (m.role === 'deferred_revenue') return refuse('UNBILLED_AR_LINE_IS_DEFERRED_REVENUE', '"' + key + '" is a deferred-revenue line -- the unbilled receivable is a current asset.');
  if ((l.accruedPayrollKey && key === l.accruedPayrollKey) || (l.accruedCommissionsKey && key === l.accruedCommissionsKey)) {
    return refuse('UNBILLED_AR_LINE_IS_ACCRUED_LINK', '"' + key + '" already carries the accrued-payroll or accrued-commissions balance.');
  }
  if (!unbilledArLeafKeysFromMeta(mm).includes(key)) {
    return refuse('UNBILLED_AR_LINE_NOT_ELIGIBLE', '"' + key + '" is not an Other receivable or Other current asset line (a trade receivable is forecast from revenue and cannot be linked).');
  }
  return null;
}
